import { codeLangNames } from "@/ts/records/codeLangNames";
import { loadScript } from "./loadScript";

const PRISM_CSS = [
  "https://cdn.jsdelivr.net/npm/prismjs@v1.x/plugins/toolbar/prism-toolbar.css"
];

const PRISM_BASE =
  "https://cdn.jsdelivr.net/npm/prismjs@v1.x/components/prism-core.min.js";
const PRISM_TOOLBAR =
  "https://cdn.jsdelivr.net/npm/prismjs@v1.x/plugins/toolbar/prism-toolbar.min.js";
const PRISM_AUTOLOADER =
  "https://cdn.jsdelivr.net/npm/prismjs@v1.x/plugins/autoloader/prism-autoloader.min.js";

/**
 * Add a title header above the code block with copy functionality.
 * @param codeBlock Element containing code.
 * @returns Returns early if a header has already been created.
 */
const insertPrismHeader = (codeBlock: HTMLElement) => {
  const classList = Array.from(codeBlock.classList);
  const langClass = classList.find(cls => cls.startsWith("language"));

  const lang = langClass
    ? langClass.replace("language-", "").toLowerCase()
    : "Code";

  // If a header already exists - update it.
  const prev = codeBlock.previousElementSibling;
  if (prev?.classList.contains("code-header")) {
    prev.querySelector(".code-header__language")!.textContent = lang;
    return;
  }

  // Remove Prism's toolbar if it exists
  const parent = codeBlock.parentElement;
  const existingToolbar = parent?.querySelector(":scope > .toolbar");
  if (existingToolbar) {
    existingToolbar.remove();
  }

  // Create header
  const header = document.createElement("div");
  header.className = "code-header";

  const label = document.createElement("span");
  label.className = "code-header__language";
  label.textContent = codeLangNames[lang] || lang;
  header.appendChild(label);

  // Always create our own toolbar with click handler
  const toolbar = document.createElement("div");
  toolbar.className = "toolbar";

  const toolbarItem = document.createElement("div");
  toolbarItem.className = "toolbar-item";

  const copyButton = document.createElement("button");
  copyButton.className = "copy-to-clipboard-button";
  copyButton.type = "button";
  copyButton.setAttribute("data-copy-state", "copy");

  const buttonSpan = document.createElement("span");
  buttonSpan.textContent = "Copy";
  copyButton.appendChild(buttonSpan);

  copyButton.addEventListener("click", () => {
    const code =
      codeBlock.querySelector("code")?.textContent ||
      codeBlock.textContent ||
      "";

    // Copy to clipboard with fallback for iframes
    const copyToClipboard = (text: string) => {
      // Try modern API first
      if (navigator.clipboard?.writeText) {
        return navigator.clipboard.writeText(text);
      }
      // Fallback for iframes or older browsers
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      return Promise.resolve();
    };

    copyToClipboard(code);

    // Update button text immediately
    copyButton.setAttribute("data-copy-state", "copy-success");
    buttonSpan.textContent = "Copied!";

    // Reset after 5 seconds
    setTimeout(() => {
      copyButton.setAttribute("data-copy-state", "copy");
      buttonSpan.textContent = "Copy";
    }, 5000);
  });

  toolbarItem.appendChild(copyButton);
  toolbar.appendChild(toolbarItem);
  header.appendChild(toolbar);

  codeBlock.before(header);
};

/**
 * Remove any formatting applied by content from Learnosity, add a title, and markup as required.
 * @param codeBlock Element containing code.
 * @returns Returns early if codeblock is already initialised.
 */
const cleanCodeblock = (codeBlock: HTMLElement) => {
  if (codeBlock.dataset.prismInitialised === "true") return;

  codeBlock.dataset.prismInitialised = "true";

  // Insert header first (this will grab Prism's toolbar if it exists)
  insertPrismHeader(codeBlock);

  // Unwrap from code-toolbar if present
  const container = codeBlock.parentElement;
  if (container?.classList.contains("code-toolbar")) {
    // Move the header outside the code-toolbar before unwrapping
    const header = container.querySelector(".code-header");
    if (header) {
      container.before(header);
    }
    container.replaceWith(codeBlock);
  }
};

/**
 * Removes the container element if it doesn't contain the expected Prism `pre` element.
 * @param toolbar Element to check
 */
const removeEmptyToolbar = (toolbar: HTMLElement) => {
  const codeBlock = toolbar.querySelectorAll("pre[class*='language-']");
  if (codeBlock.length === 0) {
    toolbar.remove();
  }
};

window.__rendering = window.__rendering || {};

window.__rendering.prism = {
  ready: false,
  loading: false,

  load: async () => {
    const api = window.__rendering!.prism!;

    if (api.ready || api.loading) return;

    api.loading = true;

    // Load CSS files
    for (const href of PRISM_CSS) {
      const cssLink = document.createElement("link");
      cssLink.rel = "stylesheet";
      cssLink.href = href;
      document.head.appendChild(cssLink);
    }

    await loadScript(PRISM_BASE).then(async () => {
      // Load plugins after core is loaded.
      try {
        // 1. Load toolbar first.
        await loadScript(PRISM_TOOLBAR);
        // 2. Autoloader (load last).
        return await loadScript(PRISM_AUTOLOADER);
      } catch (error) {
        console.error("Error loading Prism plugins:", error);
      }
    });

    api.ready = true;
    api.loading = false;
  },

  render: (container: HTMLElement | string) => {
    const api = window.__rendering!.prism!;

    if (!api.ready || !window.Prism) return;

    window.Prism.manual = true;

    const el =
      typeof container === "string"
        ? document.getElementById(container)
        : container;

    if (!el) return;

    requestAnimationFrame(() => {
      setTimeout(() => {
        // Remove any empty toolbars first
        const existingToolbars = el.querySelectorAll("div.code-toolbar");
        for (const toolbar of existingToolbars) {
          removeEmptyToolbar(toolbar as HTMLElement);
        }

        // Let Prism highlight the code
        window.Prism!.highlightAllUnder(el);

        // After Prism finishes, clean up and add our custom headers
        setTimeout(() => {
          const codeBlocks = el.querySelectorAll("pre[class*='language-']");
          for (const codeBlock of codeBlocks) {
            cleanCodeblock(codeBlock as HTMLElement);
          }
        }, 500);
      }, 500);
    });
  }
};
